import { DragEndEvent } from '@dnd-kit/core';
import { Canceler } from 'axios';
import { action, computed, makeObservable, observable } from 'mobx';
import { defaultDeal, defaultDealValidation } from '../constants/deals';
import { IDealsItemValidation, IDealsValidation } from '../types/deals';
import { ITEM_TYPE, LOCALES, MEASURE, defaultPagination } from 'src/constants/main';
import { SCREENS } from 'src/constants/screens';
import { localeStore } from 'src/mobx/localesStore';
import client from 'src/services/client';
import { history } from 'src/services/history';
import { IDeal, IDealItem } from 'src/types/combo';
import { ICountsResponce, IPagination, TBooleanInNumber } from 'src/types/main';
import { PRODUCT_TYPE } from 'src/types/product';
import { booleanNumberReverse, countsUpdate, getAvailabilityParams, getCancelToken, getTranslate, scrollToErrorElement } from 'src/utils/helpers';
import { emptyNameValidation, emptyPriceValidation, sameNameValidation, validateItemsOrderType } from 'src/utils/validation';
import { defaultItemsFilters } from 'src/view/screens/MenuManagementScreen/screens/ItemsScreen/constants/items';
import { IItemsFilters } from 'src/view/screens/MenuManagementScreen/screens/ItemsScreen/types/items';
import logger from '~/services/logger';
import { toastSuccess } from '~/utils/toasts';

class DealsStore {
  constructor() {
    makeObservable(this);
  }

  cancelStack: {
    [key: string]: Canceler;
  } = {};

  @observable pagination: IPagination<IDeal> = defaultPagination;
  @observable deal: IDeal | null = null;
  @observable edited: boolean = false;
  @observable filters: IItemsFilters = defaultItemsFilters;
  @action setFilters = (filters: Partial<IItemsFilters>) => {
    this.filters = { ...this.filters, ...filters };
    this.pagination.current_page = 0;
    this.fetchDeals(1);
  };

  @computed get isLoading(): boolean {
    return Boolean(this.pagination.loading || this.pagination.refreshing);
  }

  @action fetchDeals = async (page: number = this.pagination.current_page + 1) => {
    try {
      const cancelId = 'fetchDeals';
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      const params: any = {
        page,
        name: this.filters.search,
        is_delete: 0,
        category_id: this.filters.category?.value || undefined,
        sort_field: this.filters.sort_field,
        sort_dir: this.filters.sort_dir,
        types: [PRODUCT_TYPE.DEAL_COMBO],
        ...getAvailabilityParams(this.filters.availability),
      };

      this.pagination.loading = true;
      this.pagination.refreshing = true;
      const pagination = await client.get('v4/products', {
        params,
        headers: { 'Accept-Language': 'en,ar' },
        cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)),
      });

      this.pagination = {
        ...pagination,
        loading: false,
        refreshing: false,
      };
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination = {
        ...this.pagination,
        loading: false,
        refreshing: false,
      };
      logger.error({ error, place: 'DealsStore fetchDeals' });
    }
  };

  public dealsCount: number | undefined = undefined;
  public foundDealsInSystem: boolean = false;
  public fetchDealsCount = async () => {
    try {
      const counts: ICountsResponce = await client.get(`v4/products_count`, { params: { types: [PRODUCT_TYPE.DEAL_COMBO] } });
      this.dealsCount = counts.available;
      this.foundDealsInSystem = Boolean(counts.available + counts.deleted);
    } catch (error: any) {
      logger.error({ error, place: 'DealsStore fetchDealsCount' });
    }
  };

  @action getDeal = async (id: string | number) => {
    try {
      this.pagination.loading = true;
      const deal: IDeal = await client.get(`v4/products/${id}`, { headers: { 'Accept-Language': 'en,ar' } });
      this.setDeal(deal && deal.id ? this.transformDeal(deal) : undefined);
      this.pagination.loading = false;
    } catch (error: any) {
      this.pagination.loading = false;
      logger.error({ error, place: 'DealsStore getDeal', showModal: true });
    }
  };

  @action setDeal = (deal?: IDeal) => {
    this.deal = deal ? this.transformDeal(deal) : this.defaultDeal;

    if (this.deal.items) {
      dealsStore.validateItemsOrderType();
    }

    if (this.deal.images?.length) {
      this.images = [this.deal.image, ...this.deal.images];
    } else {
      this.images = this.deal.image ? [this.deal.image] : undefined;
    }
  };
  private get defaultDeal(): IDeal {
    const count: string = String((this.dealsCount ?? this.pagination.total) + 1);

    return {
      ...defaultDeal,
      name: {
        en: getTranslate('combo_offer', LOCALES.EN) + ' ' + count,
        ar: getTranslate('combo_offer', LOCALES.AR) + ' ' + count,
      },
    };
  }
  private transformDeal = (deal: IDeal): IDeal => {
    return {
      ...deal,
      external_id: deal.external_id?.toString(),
      price: deal.price.toString(),
      weight: deal.weight.toString(),
      unit: deal.unit || MEASURE.NULL,
    };
  };

  // images block
  @observable images: string[] | undefined = undefined;
  @action uploadImage = (image: string) => {
    if (!this.images) this.images = [image];
    else this.images.push(image);
    this.edited = true;
  };
  @action deleteImage = (image: string) => {
    if (!this.images) return;

    this.images = this.images.filter((_image) => _image !== image);
    this.edited = true;
  };
  @action imagesChangesSubmit = (reset?: boolean) => {
    if (!this.deal) return;

    if (reset) {
      this.images = this.deal.is_draft ? undefined : [this.deal.image, ...(this.deal.images || [])];
    } else {
      return;
      // this.images = this.deal.image ? [this.deal.image] : undefined;
    }
  };
  // from DragAndDrop
  @observable draggableImage: string | null = null;
  @action setDraggableImage = (image: string | null) => {
    this.draggableImage = image ? image : null;
  };
  public handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!this.images) return;

    if (over && over.id !== 'image-overlay' && active.id !== over.id) {
      this.images = this.images.map((image) => {
        if (image === over.id) return active.id;
        if (image === active.id) return over.id;
        return image;
      });
      this.edited = true;
    }
    this.setDraggableImage(null);
  };

  // main details block
  @action changeDealName = (value: string, currentLanguage: LOCALES) => {
    if (!this.deal) return;

    this.deal.name = { ...this.deal.name, [currentLanguage]: value };
    this.edited = true;
    // update validation
    if (!this.validation.name.enValid || !this.validation.name.arValid) this.setValidation();
  };
  @action changeDealDescription = (value: string, currentLanguage: LOCALES) => {
    if (!this.deal) return;

    this.deal.description = { ...this.deal.description, [currentLanguage]: value };
    this.edited = true;
  };
  @action changeDealHandler = (value: Partial<IDeal>) => {
    if (!this.deal) return;

    this.deal = { ...this.deal, ...value };
    this.edited = true;
  };

  // items block
  @action addItem = (items: IDealItem[]) => {
    if (!this.deal) return;
    this.deal.items = items;
    // check combo items order type conflict
    this.validateItemsOrderType();
    // update DealPrice
    this.recalculationDealPrice(true);
    // update items discount prices
    this.recalculationDealItemPrice();
    // update validations
    this.setValidation();
  };
  @action deleteItem = (id: number) => {
    if (!this.deal?.items) return;

    this.deal.items = this.deal.items.filter((item) => item.id !== id);
    this.edited = true;
    // check combo items order type conflict
    this.validateItemsOrderType();
    // update DealPrice
    this.recalculationDealPrice(true);
    // update items discount prices
    this.recalculationDealItemPrice();
  };
  @action updateItemDiscountPrice = (id: number, value: string) => {
    if (!this.deal?.items) return;

    this.deal.items = this.deal.items.map((item) => {
      return item.id === id ? { ...item, price: value } : item;
    });
    this.recalculationDealItemPrice(id);
    this.edited = true;
    // update DealPrice
    this.recalculationDealPrice();
  };

  /**
   * calculate block
   *
   * dealPrice - deal combo price
   * dealItemPrice - discount item price
   * discountPrices - sum deal items discount prices
   * globalPrices - sum deal items global prices
   *
   * documentation - taker.atlassian.net/wiki/spaces/T2F/pages/111181914/Combo+offer#Price-calculation-formulas-and-rules
   *
   * @memberof DealsStore
   */
  @action recalculationDealPrice = (recalc?: boolean) => {
    if (!this.deal || !this.deal.items?.length) return;
    if (!recalc) {
      if (this.deal.price) return;
    }
    let dealComboPrice: number = 0;
    this.deal.items.map((item) => (dealComboPrice += Number(item.product_price || 0)));
    this.deal.price = dealComboPrice.toString();
  };

  @action recalculationDealItemPrice = (id?: number) => {
    if (!this.deal || !this.deal.price || !this.deal.items?.length || this.deal.items?.length < 2) return;
    let discountPrices: number = 0;
    let globalPrices: number = 0;
    const lastDealItem: IDealItem | undefined = this.deal.items[this.deal.items.length - 1];

    if (lastDealItem.id === id) {
      this.deal.items.forEach((item) => (discountPrices += Number(item.price || 0)));
      this.changeDealHandler({ price: discountPrices.toString() });
      return;
    }

    // get summ items global prices
    this.deal.items.forEach((item) => (globalPrices += Number(item.product_price || 0)));

    // recalculate price of all items, except for the changed one
    this.deal.items.map((item) => {
      if (item.id === id) return item;
      return (item.price = Math.round((Number(this.deal?.price || 0) / globalPrices) * Number(item.product_price || item.price)).toString());
    });

    // recalculate last item discount price
    if (lastDealItem && lastDealItem.id !== id) {
      this.deal.items.forEach((item) => (discountPrices += Number(item.price || 0)));
      discountPrices = discountPrices - Number(lastDealItem.price);
      const lastItemDiscountPrice: number = Number(this.deal?.price || 0) - discountPrices;
      this.deal.items[this.deal.items.length - 1] = {
        ...lastDealItem,
        price: lastItemDiscountPrice >= 0 ? lastItemDiscountPrice.toString() : '0',
      };
    }
  };
  /**
   * calculate block end
   */

  @action createDeal = async () => {
    try {
      if (!this.deal) return;

      this.pagination.loading = true;

      await client.post(`v4/products`, this.params);

      this.edited = false;
      history.push(SCREENS.DEALS());

      toastSuccess({ message: localeStore.t('toast_combo_deal_changes_saved') });
    } catch (error: any) {
      this.pagination.loading = false;

      // name validation
      [this.validation.name, this.validation.valid] = sameNameValidation({ name: this.validation.name, type: ITEM_TYPE.DEAL_COMBO, error });
      if (!this.validation.valid) {
        scrollToErrorElement();
        return;
      }

      logger.error({ error, place: 'DealsStore createDeal', showModal: true });
    }
  };

  @action updateDeal = async (id: number) => {
    try {
      if (!this.deal) return;

      this.pagination.loading = true;

      await client.put(`v4/products/${id}`, this.params);

      this.edited = false;
      history.push(SCREENS.DEALS());

      toastSuccess({ message: localeStore.t('toast_combo_deal_changes_saved') });
    } catch (error: any) {
      this.pagination.loading = false;

      // name validation
      [this.validation.name, this.validation.valid] = sameNameValidation({ name: this.validation.name, type: ITEM_TYPE.DEAL_COMBO, error });
      if (!this.validation.valid) {
        scrollToErrorElement();
        return;
      }

      logger.error({ error, place: 'DealsStore updateDeal', showModal: true });
    }
  };
  private get params() {
    if (!this.deal) return {};

    return {
      ...this.deal,
      types: [PRODUCT_TYPE.DEAL_COMBO],
      id: this.deal.id || undefined,
      external_id: this.deal.external_id || undefined,
      category_id: this.deal.category?.id,
      description: this.deal.description ? this.deal.description : { en: null, ar: null },
      weight: this.deal.weight || 0,
      sort: Number(this.deal.sort) || 0,
      image: this.images?.length ? this.images[0] : null,
      images: this.images?.slice(1),
      is_draft: undefined,
    };
  }

  @action changeStock = async (id: number, stock: TBooleanInNumber) => {
    try {
      const cancelId = `stock_${id}`;
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');
      stock = booleanNumberReverse(stock);

      this.pagination.data = this.pagination.data.map((deal) => (deal.id === id ? { ...deal, out_of_stock: stock } : deal));

      await client.put(
        `v4/products/${id}`,
        { out_of_stock: stock, apply_availability_to_all: 1 },
        { cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)) }
      );

      toastSuccess({ message: localeStore.t('toast_combo_deal_changes_saved') });
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination.data = this.pagination.data.map((deal) =>
        deal.id === id ? { ...deal, out_of_stock: booleanNumberReverse(deal.out_of_stock) } : deal
      );
      logger.error({ error, place: 'DealsStore changeStock' });
    }
  };

  @action changeStatus = async (id: number, status: TBooleanInNumber) => {
    try {
      const cancelId = `status_${id}`;
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      this.pagination.data = this.pagination.data.map((deal) => (deal.id === id ? { ...deal, status } : deal));

      await client.put(
        `v4/products/${id}`,
        { status, apply_availability_to_all: 1 },
        { cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)) }
      );

      toastSuccess({ message: localeStore.t('toast_combo_deal_changes_saved') });
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination.data = this.pagination.data.map((deal) => (deal.id === id ? { ...deal, status: booleanNumberReverse(deal.status) } : deal));
      logger.error({ error, place: 'DealsStore changeStatus' });
    }
  };

  @action restoreDeal = async (id: number) => {
    try {
      const cancelId = `restore_${id}`;
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      this.pagination.refreshing = true;

      await client.patch(
        `v4/products/${id}`,
        { type: PRODUCT_TYPE.DEAL_COMBO },
        { cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)) }
      );

      this.fetchDeals(this.pagination.current_page);
      this.dealsCount = countsUpdate(this.dealsCount, true);

      toastSuccess({ message: localeStore.t('toast_combo_deal_restored') });
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination.refreshing = false;
      logger.error({ error, place: 'DealsStore restoreDeal', showModal: true });
    }
  };

  @action deleteDeal = async (id: number) => {
    try {
      const cancelId = `delete_${id}`;
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      this.pagination.refreshing = true;

      await client.delete(`v4/products/${id}`, { cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)) });

      this.fetchDeals(this.pagination.current_page);
      this.dealsCount = countsUpdate(this.dealsCount);

      toastSuccess({ message: localeStore.t('toast_combo_deal_deleted') });
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination.refreshing = false;
      logger.error({ error, place: 'DealsStore deleteDeal', showModal: true });
    }
  };

  @observable isConflictedItemsOrderType: boolean = false;
  @action validateItemsOrderType = () => {
    const { items } = this.deal || {};
    if (!items) return;

    this.isConflictedItemsOrderType = validateItemsOrderType(items);
  };

  @observable validation: IDealsValidation = defaultDealValidation;
  @action setValidation = () => {
    if (!this.deal) return;

    this.validation = { ...defaultDealValidation };

    // images validation
    // if (!this.images?.length) {
    //   this.validation.images = false;
    //   this.validation.valid = false;
    // }

    // name validation
    [this.validation.name, this.validation.valid] = emptyNameValidation(this.deal.name, this.validation.name, this.validation.valid);

    // category validation
    if (!this.deal.category) {
      this.validation.valid = false;
      this.validation.category = false;
      this.validation.categoryErrorText = localeStore.t('required_field_error');
    }

    // items length validation
    if (!this.deal.items?.length || (this.deal.items?.length || 0) < 2) {
      this.validation.valid = false;
      this.validation.itemsLength = false;
    }

    // deal pricesBalance validation
    let sumItemsPrice: number = 0;
    this.deal.items?.forEach((item) => (sumItemsPrice += Number(item.price) || 0));
    if (sumItemsPrice > Number(this.deal.price)) {
      this.validation.valid = false;
      this.validation.pricesBalance = false;
    }

    // price validation
    [this.validation.price, this.validation.valid] = emptyPriceValidation(this.deal.price, this.validation.price, this.validation.valid);

    // item discount price validation
    if (this.deal.items?.length) {
      this.validation.items = this.deal.items.map((item) => {
        const _item: IDealsItemValidation = { id: item.id || 0, price: { state: true, errorText: undefined } };

        // sizes price validation
        [_item.price, this.validation.valid] = emptyPriceValidation(item.price, _item.price, this.validation.valid);

        return _item;
      });
    }
  };

  @action unMount = () => {
    this.deal = null;
    this.images = undefined;
    this.edited = false;
    this.validation = defaultDealValidation;

    if (!window.location.pathname.includes(SCREENS.DEALS())) this.filters = defaultItemsFilters;
  };
}

const dealsStore = new DealsStore();

export default dealsStore;
